import { apiClient } from './apiClient';
import type { LoginRequest, LoginResponse, RegisterRequest, User, ApiResponse } from '../types/api';

class AuthService {
  /**
   * Login user with username and password
   */
  async login(credentials: LoginRequest): Promise<LoginResponse> {
    try {
      const response = await apiClient.post<any>('/auth/login', credentials);

      // Handle server response format: { success, data: { user, token }, message }
      if (response.success && response.data) {
        return {
          success: response.success,
          user: response.data.user,
          token: response.data.token,
          message: response.message
        };
      }

      // Fallback for direct format
      return response.data || response;
    } catch (error: any) {
      throw error;
    }
  }

  /**
   * Register new user
   */
  async register(userData: RegisterRequest): Promise<ApiResponse<User>> {
    try {
      const response = await apiClient.post<User>('/auth/register', userData);
      return response;
    } catch (error: any) {
      throw error;
    }
  }

  /**
   * Verify if token is still valid
   */
  async verifyToken(token: string): Promise<ApiResponse<User>> {
    try {
      // Temporarily set the token for this request
      const originalAuth = apiClient['client'].defaults.headers.common['Authorization'];
      apiClient['client'].defaults.headers.common['Authorization'] = `Bearer ${token}`;

      const response = await apiClient.get<User>('/auth/me');

      // Restore original auth header
      if (originalAuth) {
        apiClient['client'].defaults.headers.common['Authorization'] = originalAuth;
      } else {
        delete apiClient['client'].defaults.headers.common['Authorization'];
      }

      return response;
    } catch (error: any) {
      throw error;
    }
  }

  /**
   * Refresh authentication token
   */
  async refreshToken(token: string): Promise<LoginResponse> {
    try {
      // Temporarily set the token for this request
      const originalAuth = apiClient['client'].defaults.headers.common['Authorization'];
      apiClient['client'].defaults.headers.common['Authorization'] = `Bearer ${token}`;
      
      const response = await apiClient.post<LoginResponse>('/auth/refresh');
      
      // Restore original auth header
      if (originalAuth) {
        apiClient['client'].defaults.headers.common['Authorization'] = originalAuth;
      } else {
        delete apiClient['client'].defaults.headers.common['Authorization'];
      }
      
      return response.data || response;
    } catch (error: any) {
      throw error;
    }
  }

  /**
   * Logout user (client-side cleanup)
   */
  logout(): void {
    localStorage.removeItem('auth_token');
    localStorage.removeItem('auth_user');
    delete apiClient['client'].defaults.headers.common['Authorization'];
  }

  /**
   * Get current user from localStorage
   */
  getCurrentUser(): User | null {
    try {
      const userStr = localStorage.getItem('auth_user');
      return userStr ? JSON.parse(userStr) : null;
    } catch (error) {
      console.error('Error parsing user from localStorage:', error);
      return null;
    }
  }

  /**
   * Get current token from localStorage
   */
  getCurrentToken(): string | null {
    return localStorage.getItem('auth_token');
  }

  /**
   * Check if user is authenticated
   */
  isAuthenticated(): boolean {
    const token = this.getCurrentToken();
    const user = this.getCurrentUser();
    return !!(token && user);
  }

  /**
   * Check if user has specific scope
   */
  hasScope(scope: string): boolean {
    const user = this.getCurrentUser();
    if (!user || !user.scopes) return false;
    return user.scopes.includes(scope as any) || user.scopes.includes('admin');
  }

  /**
   * Check if user is developer
   */
  isDeveloper(): boolean {
    return this.hasScope('dev');
  }

  /**
   * Check if user is admin
   */
  isAdmin(): boolean {
    return this.hasScope('admin');
  }

  /**
   * Check if user is regular user
   */
  isUser(): boolean {
    return this.hasScope('user');
  }

  /**
   * Get user's highest privilege level
   */
  getUserRole(): 'dev' | 'admin' | 'user' | null {
    const user = this.getCurrentUser();
    if (!user || !user.scopes) return null;
    
    if (user.scopes.includes('dev')) return 'dev';
    if (user.scopes.includes('admin')) return 'admin';
    if (user.scopes.includes('user')) return 'user';
    
    return null;
  }

  /**
   * Set authentication token and user data
   */
  setAuthData(token: string, user: User): void {
    localStorage.setItem('auth_token', token);
    localStorage.setItem('auth_user', JSON.stringify(user));
    apiClient.setDefaultHeader('Authorization', `Bearer ${token}`);
  }

  /**
   * Clear authentication data
   */
  clearAuthData(): void {
    localStorage.removeItem('auth_token');
    localStorage.removeItem('auth_user');
    apiClient.removeDefaultHeader('Authorization');
  }
}

// Create and export singleton instance
export const authService = new AuthService();
export default authService;
